import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { LandingNavbarComponent } from '../../components/landing-navbar/landing-navbar.component';
import { ApiServiceService } from '../../modules/services/api-service.service';
import { ForgotPasswordRequest } from '../registration-page/registration-model';

type ForgotPasswordResponse = {
  status?: string;
  message?: string;
};

@Component({
  selector: 'app-forgot-password-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    LandingNavbarComponent,
  ],
  templateUrl: './forgot-password-page.html',
  styleUrl: './forgot-password-page.scss',
})
export class ForgotPasswordPage implements AfterViewInit, OnDestroy {
  readonly forgotPasswordForm;
  isSubmitting = false;
  isSuccessModalOpen = false;
  submittedEmail = '';
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);

    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            this.renderer.addClass(entry.target, 'visible');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const revealEls =
      this.host.nativeElement.querySelectorAll('.auth-scroll-reveal');
    revealEls.forEach((el) => this.intersectionObserver?.observe(el));
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  get emailControl() {
    return this.forgotPasswordForm.controls.email;
  }

  get emailErrorMessage(): string {
    if (!this.emailControl.touched && !this.emailControl.dirty) return '';
    if (this.emailControl.hasError('required'))
      return 'Email address is required.';
    if (this.emailControl.hasError('email'))
      return 'Enter a valid email address.';
    return '';
  }

  get canSubmit(): boolean {
    return this.forgotPasswordForm.valid && !this.isSubmitting;
  }

  get successMessage(): string {
    return `A password reset email has been sent to ${this.submittedEmail}.`;
  }

  submit(): void {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      alertify.error('Enter your email address to continue.');
      return;
    }

    const payload: ForgotPasswordRequest =
      this.forgotPasswordForm.getRawValue();

    this.isSubmitting = true;

    this.apiService.forgotPassword(payload).subscribe({
      next: (response: ForgotPasswordResponse) => {
        if (response.status && response.status !== 'success') {
          alertify.error(
            response.message || 'We could not send the reset email.',
          );
          this.isSubmitting = false;
          return;
        }

        this.submittedEmail = payload.email;
        this.isSuccessModalOpen = true;
        this.isSubmitting = false;
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'We could not send the reset email. Please try again.';
        alertify.error(message);
        this.isSubmitting = false;
      },
    });
  }

  closeSuccessModal(): void {
    this.isSuccessModalOpen = false;
    void this.router.navigate(['/login']);
  }
}
